import { ApiError, API_BASE_URL, apiRequest, getAccessToken } from './client'
import type { OrderBlankRequest } from './orderBlank'
import { formatUnitLabel } from '../utils/formatUnitLabel'

export type SavedOrderBlankLineDto = {
  id: number
  catalog_item_id: number | null
  name: string
  brand: string | null
  quantity: number
  unit: string | null
  unit_price: number | null
  line_total: number | null
  note: string | null
}

export type SavedOrderBlankDto = {
  id: number
  organization_id: number
  private_supplier_id: number | null
  supplier_name: string
  supplier_inn: string | null
  buyer_name: string
  delivery_address: string | null
  desired_delivery_date: string | null
  desired_delivery_time_until: string | null
  comment: string | null
  line_count: number
  total_amount: number | null
  created_at: string
  lines: SavedOrderBlankLineDto[]
}

const toBody = (request: OrderBlankRequest) => ({
  organization_id: request.organizationId,
  private_supplier_id: request.privateSupplierId,
  delivery_address: request.deliveryAddress ?? null,
  desired_delivery_date: request.desiredDeliveryDate ?? null,
  desired_delivery_time_until: request.desiredDeliveryTimeUntil ?? null,
  comment: request.comment ?? null,
  lines: request.lines.map((line) => ({
    catalog_item_id: line.catalogItemId,
    quantity: line.quantity,
    unit: formatUnitLabel(line.unit),
    ...(line.unitPrice != null ? { unit_price: line.unitPrice } : {}),
    ...(line.note?.trim() ? { note: line.note.trim() } : {}),
  })),
})

export const saveOrderBlank = (request: OrderBlankRequest) =>
  apiRequest<SavedOrderBlankDto>('/buyer/order-blanks', {
    method: 'POST',
    body: toBody(request),
  })

export const listSavedOrderBlanks = (organizationId: number) =>
  apiRequest<SavedOrderBlankDto[]>(`/buyer/order-blanks?organizationId=${organizationId}`)

export const getSavedOrderBlank = (organizationId: number, id: number) =>
  apiRequest<SavedOrderBlankDto>(`/buyer/order-blanks/${id}?organizationId=${organizationId}`)

export const fetchSavedOrderBlankText = async (
  organizationId: number,
  id: number,
): Promise<string> => {
  const token = getAccessToken()
  const response = await fetch(
    `${API_BASE_URL}/buyer/order-blanks/${id}/export?organizationId=${organizationId}&format=text`,
    {
      headers: {
        Accept: 'text/plain;charset=UTF-8',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  )
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new ApiError(response.status, text || response.statusText)
  }
  const bytes = await response.arrayBuffer()
  return new TextDecoder('utf-8').decode(bytes)
}
